import { Link } from 'react-router-dom';
import { HomeIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

export default function JointAccount() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Joint Current Account"
        description="Open a MyBank joint current account and manage shared finances with confidence. Secure day-to-day banking for couples, families and partners in Somalia."
        keywords={['joint account Somalia', 'shared bank account', 'MyBank joint current account', 'family banking']}
      />
      {/* Hero Section */}
      <div 
        className="relative min-h-[300px] sm:h-[390px] bg-gradient-to-r from-[#001F5F] to-[#017EFF] flex items-center justify-center"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-transparent" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col items-center space-y-6 sm:space-y-12 py-8 sm:py-0">
            {/* Breadcrumb */}
            <nav className="flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm text-white/90 flex-wrap">
              <Link to="/" className="hover:text-[#017EFF] transition-colors flex items-center">
                <HomeIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                Home
              </Link>
              <span className="text-white/60 mx-1 sm:mx-2">/</span>
              <Link to="/#banking-solutions" className="hover:text-[#017EFF] transition-colors hidden sm:inline">Personal/Retail Banking</Link>
              <span className="text-white/60 mx-1 sm:mx-2 hidden sm:inline">/</span>
              <Link to="/personal-current-account" className="hover:text-[#017EFF] transition-colors">Current Accounts</Link>
              <span className="text-white/60 mx-1 sm:mx-2">/</span>
              <span className="text-[#017EFF]">Joint Account</span>
            </nav>
            
            {/* Hero Content */}
            <div className="flex flex-col items-center max-w-4xl mx-auto">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight text-center mb-4 sm:mb-6 px-2">
                Joint <span className="text-[#017EFF]">Current Account</span>
              </h1>
              <p className="text-sm sm:text-lg md:text-xl text-white/90 text-center leading-relaxed px-4 max-w-3xl">
                Keep the bond strong - Secure banking for shared financial goals
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-4 -mt-8 sm:-mt-16 mb-12 sm:mb-24">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-8 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-start">
            {/* Left Column - Content */}
            <div className="space-y-6 sm:space-y-8 lg:space-y-10">
              <div className="space-y-4 sm:space-y-6">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#001F5F]">
                  Shared Banking Made Easy
                </h2>
                
                <div className="space-y-4 text-gray-700">
                  <p className="text-sm sm:text-base leading-relaxed">
                    This account is designed for people with a strong family, business, and personal relationships. It is also designed for people who are doing a small number of business transactions with their common names and responsibilities.
                  </p>
                  <p className="text-sm sm:text-base leading-relaxed">
                    It gives clients peace of mind regarding their mutual businesses, funds, and contracts as funds are accessed only as per the account operating mandates you give to the bank.
                  </p>
                  <p className="text-sm sm:text-base leading-relaxed">
                    This type of account requires minimal account operating balances with affordable yearly account maintenance fees.
                  </p>
                </div>
              </div>

              {/* Benefits List */}
              <div className="bg-gray-50 rounded-lg sm:rounded-xl p-4 sm:p-6 lg:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-[#001F5F] mb-4 sm:mb-6 lg:mb-8">
                  Account Benefits
                </h3>
                <div className="grid grid-cols-1 gap-3 sm:gap-4 lg:gap-6">
                  {[
                  "A widely accepted means of payment where you can pay Groceries, shops, Restaurants, Government agencies, transport companies, hotels, Universities, TV companies, Schools, etc.",
                  "50 leaf checkbook",
                  "Automated digital bank statement sent on a monthly basis",
                  "Convenient and reliable incoming & outgoing payments/remittances through Swift and other remittance channels and payment gateways.",
                  "Convenient bills payments (Water, Electricity, TVs, Schools, Universities, Visa, Business license, taxes, and other government payments, etc.)",
                  "Internet and Mobile Banking",
                  "SMS notifications of all transactions hitting your account",
                  "Easy, 24/7 live chat with the bank support team through our website, WhatsApp and other Social Media platforms",
                  "Assigned relationship manager",
                  "Eligibility for different financing products"

                  ].map((benefit, index) => (
                    <div key={index} className="flex items-start text-gray-700">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 sm:w-5 sm:h-5 text-[#017EFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-sm sm:text-base">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Button */}
              <div className="pt-2 sm:pt-4">
                <Link
                  to="/open-account"
                  className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 bg-[#017EFF] text-white font-semibold rounded-lg hover:bg-[#0165CC] transition-all transform hover:scale-105 hover:shadow-lg text-sm sm:text-base w-full sm:w-auto justify-center"
                >
                  Open Joint Account
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Right Column - Requirements */}
            <div className="space-y-6 sm:space-y-8 mt-8 lg:mt-0">
              {/* Requirements Section */}
              <div className="bg-[#001F5F] rounded-lg sm:rounded-xl p-4 sm:p-6 lg:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-4 sm:mb-6 lg:mb-8">
                  Requirements
                </h3>
                <div className="space-y-4 sm:space-y-6">
                  {[
                    "Valid, certified copy of National ID, passport or any other ID document approved by the Central Bank of Somalia of the joint account holders",
                    "Physical addresses of the joint account holders",
                    "Proper declaration and documentation of the joint account holders' source/volumes of income, nature of business and purpose of account opening",
                    "Two passport size recent photos of the account holders and account signatories if they are different from the account holders",
                    "Request letter, resolution, or extract of meeting minutes from the account holders requesting to open the said account and giving clear instruction regarding the account signing mandate"
                  ].map((requirement, index) => (
                    <div key={index} className="flex items-start text-white/90">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-[#017EFF]/20 flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 sm:w-5 sm:h-5 text-[#017EFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-sm sm:text-base">{requirement}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 